import { useCallback } from 'react';

import { useAppState } from '@garnish/core';
import { useActorRef } from '@xstate/react';
import { type ActorRefFrom } from 'xstate';

import { createOtaMachineReporter, otaMachine } from '@sg/ota-installer';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { otaLogger } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitOtaActor = (): ActorRefFrom<typeof otaMachine> => {
  const appState = useAppState();

  const pollingIntervalInMinutes = useFeatureFlag('CELS-4466-order-in-app-ota-polling-interval');

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfAppIsReady = useCallback(() => appState === 'active', [appState]);

  // ─── Refs ────────────────────────────────────────────────────────────

  return useActorRef(
    otaMachine.provide({
      actions: otaMachineActions,
      delays: { POLLING_INTERVAL: pollingIntervalInMinutes * 60 * 1000 },
      guards: { 'check-if-app-is-ready': checkIfAppIsReady },
    }),
    { input: { logger: otaLogger, shouldUseLaunchTimeGate: true } },
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const otaMachineActions = createOtaMachineReporter();
